import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import client from '../api/client';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('armundi_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      client.get('/api/armundi/auth/me')
        .then(r => {
          const u = r.data;
          setUser({
            ...u,
            name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
          });
        })
        .catch(() => { setToken(null); localStorage.removeItem('armundi_token'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email: string, password: string) => {
    const r = await client.post('/api/armundi/auth/login', { email, password });
    const { token: t, user: u } = r.data;
    const normalizedUser = {
      ...u,
      name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
    };
    localStorage.setItem('armundi_token', t);
    setToken(t);
    setUser(normalizedUser);
  };

  const logout = () => {
    localStorage.removeItem('armundi_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
